import React, { useState, useEffect } from 'react';
import { apiClient } from '../utils/apiClient';

/**
 * Daily-Claim-Banner — sticky direkt unter der Kopfleiste.
 * - Pre-Claim:  Grüner Banner mit „Jetzt claimen" + Countdown bis Verfall (Berlin-Mitternacht).
 * - Post-Claim: Kompakter Banner mit „Nächstes Gratis-Bild in HH:MM:SS".
 * - Wegklicken (X) merkt sich's bis zur nächsten Berlin-Mitternacht (localStorage).
 */
const DISMISS_KEY = 'daily_claim_dismissed_until';

export default function DailyClaimBanner() {
  const [canClaim, setCanClaim] = useState(false);
  const [nextResetAt, setNextResetAt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [headerHeight, setHeaderHeight] = useState(56);

  // Header-Höhe dynamisch messen — variiert mit Impersonation-Banner
  useEffect(() => {
    const measure = () => {
      const el = document.getElementById('app-header');
      if (el) setHeaderHeight(el.offsetHeight || 56);
    };
    measure();
    let ro;
    const el = document.getElementById('app-header');
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Initial-Status laden + Dismiss-Persistenz aus localStorage prüfen
  useEffect(() => {
    const token = localStorage.getItem('snapshot_token');
    if (!token) return;
    try {
      const dUntil = localStorage.getItem(DISMISS_KEY);
      if (dUntil && new Date(dUntil).getTime() > Date.now()) {
        setDismissed(true);
      } else if (dUntil) {
        // Abgelaufen — aufräumen
        localStorage.removeItem(DISMISS_KEY);
      }
    } catch (e) {}

    (async () => {
      try {
        const res = await apiClient.get('/api/auth/daily-claim-status');
        if (res.ok) {
          const data = await res.json();
          setCanClaim(data.can_claim === true);
          setNextResetAt(data.next_reset_at || data.next_claim_at || null);
          const user = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
          user.daily_image_can_claim = data.can_claim === true;
          localStorage.setItem('snapshot_user', JSON.stringify(user));
        }
      } catch (e) {}
    })();
  }, []);

  // Tick-Timer für Countdown — läuft immer, solange wir einen Reset-Zeitpunkt haben
  useEffect(() => {
    if (!nextResetAt) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [nextResetAt]);

  // Wenn Countdown auf 0 läuft (= Mitternacht erreicht) → Status neu laden + Dismiss aufheben
  useEffect(() => {
    if (!nextResetAt) return;
    const diff = new Date(nextResetAt).getTime() - now;
    if (diff <= 0) {
      try { localStorage.removeItem(DISMISS_KEY); } catch (e) {}
      setDismissed(false);
      (async () => {
        try {
          const res = await apiClient.get('/api/auth/daily-claim-status');
          if (res.ok) {
            const data = await res.json();
            setCanClaim(data.can_claim === true);
            setNextResetAt(data.next_reset_at || data.next_claim_at || null);
          }
        } catch (e) {}
      })();
    }
  }, [now, nextResetAt]);

  const handleClaim = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const res = await apiClient.post('/api/auth/daily-claim');
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setClaimed(true);
          const user = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
          user.free_images = data.free_images;
          user.daily_image_can_claim = false;
          localStorage.setItem('snapshot_user', JSON.stringify(user));
          // Erfolgreich geclaimt — Dismiss aufheben, damit der Post-Claim-Banner sichtbar ist
          try { localStorage.removeItem(DISMISS_KEY); } catch (e) {}
          setDismissed(false);
          setTimeout(() => {
            setClaimed(false);
            setCanClaim(false);
          }, 3000);
        }
      }
    } catch (err) {
      console.error('Daily claim error:', err);
    } finally {
      setClaiming(false);
    }
  };

  const handleDismiss = () => {
    try {
      if (nextResetAt) localStorage.setItem(DISMISS_KEY, nextResetAt);
    } catch (e) {}
    setDismissed(true);
  };

  const formatCountdown = () => {
    if (!nextResetAt) return '';
    const diff = Math.max(0, new Date(nextResetAt).getTime() - now);
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    const s = Math.floor((diff % 60_000) / 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Banner-Höhe als zusätzliches Body-Padding setzen, sobald sichtbar.
  // Spart Spezial-Logik in jeder Hauptseite.
  const bannerVisible = !dismissed && (canClaim || !!nextResetAt);
  useEffect(() => {
    const BANNER_PX = 52; // Banner ist ca. 52 px hoch (py-2 + 36px Inhalt)
    if (bannerVisible) {
      const prev = document.body.style.paddingTop || '';
      document.body.dataset._dailyBannerPrevPad = prev;
      document.body.style.paddingTop = `${BANNER_PX}px`;
      return () => {
        document.body.style.paddingTop = document.body.dataset._dailyBannerPrevPad || '';
        delete document.body.dataset._dailyBannerPrevPad;
      };
    }
  }, [bannerVisible]);

  if (dismissed) return null;
  if (!canClaim && !nextResetAt) return null;

  // Banner sitzt direkt unter der fixed Kopfleiste (~48 px hoch, mit 1 px Border)
  // z-30 statt z-40, damit der fixed Header (z-40) IMMER drüber bleibt
  const wrapperBase = 'fixed left-0 right-0 z-30';
  const wrapperStyle = { top: `${headerHeight}px` };

  // Erfolgs-Flash (3 s nach Claim)
  if (claimed) {
    return (
      <div style={wrapperStyle} className={`${wrapperBase} bg-gradient-to-r from-[#10B981] to-[#059669] p-3 shadow-xl shadow-[#10B981]/20 animate-pulse`}>
        <div className="max-w-xl mx-auto flex items-center justify-center gap-3">
          <span className="text-xl">🎁</span>
          <p className="text-black font-bold text-sm">
            Erfolgreich gutgeschrieben! Dein Gratis-Bild ist jetzt verfügbar.
          </p>
        </div>
      </div>
    );
  }

  // Pre-Claim-Banner (claimbar) — grün, mit Verfall-Countdown
  if (canClaim) {
    return (
      <div style={wrapperStyle} className={`${wrapperBase} bg-black/95 backdrop-blur-xl border-b border-[#10B981]/30 px-3 py-2 shadow-lg`}>
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center text-lg shadow-md shadow-[#10B981]/30">
            🎁
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-[13px] leading-tight">Dein Gratis-Bild wartet!</p>
            <p className="text-white/60 text-[11px] tabular-nums">Verfällt in {formatCountdown()}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={handleClaim}
              disabled={claiming}
              className="px-3 py-1.5 bg-[#10B981] text-black font-bold text-xs rounded-lg hover:bg-[#059669] transition-all active:scale-95 shadow-md shadow-[#10B981]/30 disabled:opacity-50"
            >
              {claiming ? '...' : 'Jetzt claimen'}
            </button>
            <button
              onClick={handleDismiss}
              className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white/80 transition-colors"
              aria-label="Banner ausblenden"
            >
              <span className="text-base">×</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Post-Claim-Banner (nicht claimbar) — kompakter Countdown
  return (
    <div style={wrapperStyle} className={`${wrapperBase} bg-black/95 backdrop-blur-xl border-b border-white/10 px-3 py-2 shadow-lg`}>
      <div className="max-w-xl mx-auto flex items-center gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-lg">
          ⏳
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white/80 text-[11px] leading-tight">Nächstes Gratis-Bild in</p>
          <p className="text-white font-bold text-[13px] tabular-nums">{formatCountdown()}</p>
        </div>
        <button
          onClick={handleDismiss}
          className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white/80 transition-colors flex-shrink-0"
          aria-label="Banner ausblenden"
        >
          <span className="text-base">×</span>
        </button>
      </div>
    </div>
  );
}
